import type { CmsMenu, CmsPage, CmsPageSidebar, UpdateCmsPageRequest } from "../../types/cms";
import { type PageSection, pageSectionOf } from "./pageSection";
import { getPageType } from "./pageTypeRegistry";

export type PageSidebarUpdate = Pick<UpdateCmsPageRequest, "menuId" | "sidebar" | "sidebarName">;

export type SidebarChoice = "" | "new" | "join";

export type SidebarSelection = {
  choice: SidebarChoice;
  menuId: string;
  newName: string | null;
  rename: string | null;
};

export type SidebarOption = {
  menuId: string;
  name: string;
  label: string;
  pageCount: number;
  sections: PageSection[];
};

export const NO_SIDEBAR_SELECTION: SidebarSelection = {
  choice: "",
  menuId: "",
  newName: null,
  rename: null,
};

export function sidebarSelectionFromPage(page: CmsPage): SidebarSelection {
  if (page.menuId) {
    return { ...NO_SIDEBAR_SELECTION, choice: "join", menuId: page.menuId };
  }
  return NO_SIDEBAR_SELECTION;
}

export function isSameSidebarSelection(a: SidebarSelection, b: SidebarSelection): boolean {
  return a.choice === b.choice && a.menuId === b.menuId;
}

export function newSidebarName(selection: SidebarSelection, pageTitle: string): string {
  return (selection.newName ?? pageTitle).trim();
}

export function toSidebarRequest(
  selection: SidebarSelection,
  pageTitle: string,
): CmsPageSidebar | undefined {
  if (selection.choice === "new") {
    return { action: "new", name: newSidebarName(selection, pageTitle) };
  }
  if (selection.choice === "join" && selection.menuId) {
    return { action: "join", menuId: selection.menuId };
  }
  return undefined;
}

export function buildSidebarOptions(menus: CmsMenu[], pages: CmsPage[]): SidebarOption[] {
  return menus.map(menu => {
    const menuPages = pages.filter(page => page.menuId === menu.id);
    const titles = menuPages.map(page => page.title);
    return {
      menuId: menu.id,
      name: menu.title,
      label: titles.length > 0 ? `${menu.title} (${titles.join(", ")})` : menu.title,
      pageCount: titles.length,
      sections: [...new Set(menuPages.map(pageSectionOf))],
    };
  });
}

export function sidebarOptionsInSection(
  options: SidebarOption[],
  section: PageSection,
): SidebarOption[] {
  return options.filter(option =>
    option.sections.every(optionSection => optionSection === section),
  );
}

export function sidebarUpdateForSave(
  type: string,
  selection: SidebarSelection,
  savedPage: CmsPage,
  pageTitle: string,
): PageSidebarUpdate {
  const pageType = getPageType(type);
  if (!pageType) {
    return {};
  }
  if (!pageType.hasSubmenu) {
    return { menuId: null };
  }
  if (!isSameSidebarSelection(selection, sidebarSelectionFromPage(savedPage))) {
    return { sidebar: toSidebarRequest(selection, pageTitle) };
  }
  if (selection.rename !== null) {
    return { sidebarName: selection.rename.trim() };
  }
  return {};
}

export function listInSidebarFromPage(page: CmsPage): boolean {
  return page.listInSidebar ?? page.options?.listInSidebar !== false;
}

export function withSidebarListing(
  options: Record<string, unknown> | null,
  listInSidebar: boolean,
): Record<string, unknown> | null {
  if (listInSidebar) {
    return options;
  }
  return { ...options, listInSidebar: false };
}

export type PageListingUpdate = {
  listInSidebar?: boolean;
  options: Record<string, unknown> | null;
};

export function sidebarListingForSave(
  type: string,
  listInSidebar: boolean,
  options: Record<string, unknown> | null,
): PageListingUpdate {
  if (!getPageType(type)?.hasSubmenu) {
    return { options };
  }
  return { listInSidebar, options: withSidebarListing(options, listInSidebar) };
}
